import moment from 'moment';
import { PhaseDto } from '../../../../dtos/PhaseDto';
import { StepDto } from '../../../../dtos/StepDto';
import { PhaseStatus } from '../../../../dtos/PhaseStatus';
import { StepStatus } from '../../../../dtos/StepStatus';
import { GrantActivityItemDto } from '../../../../dtos/GrantActivityItemDto';
import { STATUS, TIMELINE } from '../../../shared/Utils';
import { MODULE_CATALOG, ProgressModule } from './moduleCatalog';
import { MergedSpan, RawProgressData } from './loadProgressData';

export type ProgressStatus =
    | typeof STATUS.SCHEDULED
    | typeof STATUS.INPROGRESS
    | typeof STATUS.COMPLETED
    | typeof STATUS.SLIGHTLYLATE
    | typeof STATUS.LATE;

export type GroupingMode = 'nested' | 'flat-prefix' | 'flat-filter';
export type DepthMode = 'modules' | 'phases' | 'steps';

export interface ProgressModuleAggregate {
    key: string;
    name: string;
    abbrev: string;
    color: string;
    moduleKey: string;
    status: ProgressStatus;
    actualStart: Date;
    actualEnd: Date;
    projectedStart: Date;
    projectedEnd: Date;
}

export interface ProgressPhase {
    key: string;
    phaseId: string;
    name: string;
    moduleKey: string;
    moduleName: string;
    moduleAbbrev: string;
    status: ProgressStatus;
    actualStart: Date;
    actualEnd: Date;
    projectedStart: Date;
    projectedEnd: Date;
    // The work moved past this phase, so its bar has no width of its own and the phase after it
    // starts where this one would have.
    collapsed: boolean;
}

export interface ProgressStep {
    key: string;
    stepId: string;
    phaseId: string;
    name: string;
    phaseKey: string;
    phaseName: string;
    moduleKey: string;
    moduleName: string;
    moduleAbbrev: string;
    status: ProgressStatus;
    actualStart: Date;
    actualEnd: Date;
    projectedStart: Date;
    projectedEnd: Date;
}

export interface TimelineEvent {
    id: string;
    moduleKey: string;
    moduleName: string;
    phaseKey: string;
    phaseName: string;
    stepKey: string | null;
    stepName: string;
    date: Date;
    label: string;
    userName: string;
    type: string;
    // CommentType enum name (e.g. 'Request', 'Response') for Comment events; null otherwise.
    commentType: string | null;
    fileId: string | null;
    fileName: string | null;
    // Resubmission round for file events: null = original submission, 1/2/... = resubmission round.
    resubmission: number | null;
    // Raw ids behind the keys above, for deep linking to the event's task/subtask.
    grantId: string;
    moduleTypeId: number | null;
    phaseId: string | null;
    stepId: string | null;
}

export interface UnscheduledPhase {
    phaseId: string;
    name: string;
    moduleKey: string;
    moduleAbbrev: string;
    moduleName: string;
}

export interface UnscheduledStep {
    stepId: string;
    phaseId: string;
    name: string;
    phaseName: string;
    moduleKey: string;
    moduleAbbrev: string;
    moduleName: string;
}

// A collapsed task the chart does not draw because its events are shown on the task that absorbed it.
export interface AbsorbedPhase {
    name: string;
    absorbedInto: string;
    moduleKey: string;
}

export interface ProgressData {
    rangeMin: Date;
    rangeMax: Date;
    modules: ProgressModule[];
    moduleAggregates: ProgressModuleAggregate[];
    phases: ProgressPhase[];
    steps: ProgressStep[];
    events: TimelineEvent[];
    unscheduledPhases: UnscheduledPhase[];
    unscheduledSteps: UnscheduledStep[];
    absorbedPhases: AbsorbedPhase[];
}

export const statusColor = (s: ProgressStatus): string => {
    switch (s) {
        case STATUS.COMPLETED: return TIMELINE.COMPLETEDCOLOR;
        case STATUS.INPROGRESS: return TIMELINE.INPROGRESSCOLOR;
        case STATUS.SLIGHTLYLATE: return TIMELINE.SLIGHTLYLATECOLOR;
        case STATUS.LATE: return TIMELINE.LATECOLOR;
        case STATUS.SCHEDULED:
        default: return TIMELINE.SCHEDULEDCOLOR;
    }
};

const lateness = (projected: Date): 'on-time' | 'slightly' | 'late' => {
    if (!moment().startOf('day').isAfter(projected)) return 'on-time';
    const months = moment().startOf('day').diff(moment(projected), 'months', true);
    return months <= 2 ? 'slightly' : 'late';
};

const resolvePhaseStatus = (dto: PhaseDto, projected: Date): ProgressStatus => {
    if (dto.status === PhaseStatus.APPROVED || dto.completionDate) return STATUS.COMPLETED;
    const started = !!dto.submissionDate || dto.status >= PhaseStatus.READY;
    if (!started) return STATUS.SCHEDULED;
    const late = lateness(projected);
    if (late === 'late') return STATUS.LATE;
    if (late === 'slightly') return STATUS.SLIGHTLYLATE;
    return STATUS.INPROGRESS;
};

const resolveStepStatus = (dto: StepDto, projected: Date): ProgressStatus => {
    if (dto.status === StepStatus.APPROVED || dto.completionDate) return STATUS.COMPLETED;
    const started = !!dto.submissionDate || dto.status >= StepStatus.READY;
    if (!started) return STATUS.SCHEDULED;
    const late = lateness(projected);
    if (late === 'late') return STATUS.LATE;
    if (late === 'slightly') return STATUS.SLIGHTLYLATE;
    return STATUS.INPROGRESS;
};

const buildPhase = (dto: PhaseDto, module: ProgressModule, merged?: MergedSpan): ProgressPhase | null => {
    if (!dto.startDate || !dto.projectedEndDate) return null;
    const projectedStart = new Date(dto.startDate);
    const projectedEnd = new Date(dto.projectedEndDate);
    // Inside a document cycle the merged span is what the bar runs on: it closes the gap a skipped
    // phase would leave. Everywhere else the phase's own dates are the span.
    const submission = merged ? merged.start : (dto.submissionDate ? new Date(dto.submissionDate) : null);
    const completion = merged ? merged.end : (dto.completionDate ? new Date(dto.completionDate) : null);
    const status = resolvePhaseStatus(dto, projectedEnd);

    const actualStart = submission ?? projectedStart;
    let actualEnd: Date;
    if (completion) actualEnd = completion;
    else if (submission && moment().isAfter(moment(projectedEnd))) actualEnd = new Date();
    else actualEnd = projectedEnd;
    if (actualStart > actualEnd) actualEnd = actualStart;

    return {
        key: `${module.key}-${dto.id}`,
        phaseId: dto.id,
        name: dto.defaultPhase?.name ?? dto.name,
        moduleKey: module.key,
        moduleName: module.name,
        moduleAbbrev: module.abbrev,
        status,
        actualStart,
        actualEnd,
        projectedStart,
        projectedEnd,
        collapsed: merged?.collapsed === true,
    };
};

// Just the phase fields buildStep needs. A task can be undated (no ProgressPhase) while its
// subtasks still carry dates, so subtask building takes this lightweight context rather than a
// full ProgressPhase — ProgressPhase satisfies it structurally.
type StepPhaseContext = Pick<ProgressPhase, 'key' | 'phaseId' | 'name' | 'moduleKey' | 'moduleName' | 'moduleAbbrev'>;

const buildStep = (dto: StepDto, phase: StepPhaseContext): ProgressStep | null => {
    const projStartRaw = dto.startDate;
    const projEndRaw = dto.projectedEndDate ?? dto.dueDate;
    if (!projStartRaw || !projEndRaw) return null;
    const projectedStart = new Date(projStartRaw);
    const projectedEnd = new Date(projEndRaw);
    const submission = dto.submissionDate ? new Date(dto.submissionDate) : null;
    const completion = dto.completionDate ? new Date(dto.completionDate) : null;
    const status = resolveStepStatus(dto, projectedEnd);

    const actualStart = submission ?? projectedStart;
    let actualEnd: Date;
    if (completion) actualEnd = completion;
    else if (submission && moment().isAfter(moment(projectedEnd))) actualEnd = new Date();
    else actualEnd = projectedEnd;
    if (actualStart > actualEnd) actualEnd = actualStart;

    return {
        key: `${phase.key}-${dto.id}`,
        stepId: dto.id,
        phaseId: phase.phaseId,
        name: dto.defaultStep?.name ?? dto.name,
        phaseKey: phase.key,
        phaseName: phase.name,
        moduleKey: phase.moduleKey,
        moduleName: phase.moduleName,
        moduleAbbrev: phase.moduleAbbrev,
        status,
        actualStart,
        actualEnd,
        projectedStart,
        projectedEnd,
    };
};

const aggregateModule = (m: ProgressModule, phases: ProgressPhase[]): ProgressModuleAggregate | null => {
    const mPhases = phases.filter(p => p.moduleKey === m.key);
    if (!mPhases.length) return null;
    const projectedStart = new Date(Math.min(...mPhases.map(p => p.projectedStart.getTime())));
    const projectedEnd = new Date(Math.max(...mPhases.map(p => p.projectedEnd.getTime())));
    const actualStart = new Date(Math.min(...mPhases.map(p => p.actualStart.getTime())));
    const actualEnd = new Date(Math.max(...mPhases.map(p => p.actualEnd.getTime())));

    // Worst-case status across child phases.
    const order: ProgressStatus[] = [STATUS.SCHEDULED, STATUS.INPROGRESS, STATUS.SLIGHTLYLATE, STATUS.LATE, STATUS.COMPLETED];
    const allCompleted = mPhases.every(p => p.status === STATUS.COMPLETED);
    let status: ProgressStatus = STATUS.SCHEDULED;
    if (allCompleted) {
        status = STATUS.COMPLETED;
    } else {
        const nonCompleted = mPhases.filter(p => p.status !== STATUS.COMPLETED);
        status = nonCompleted.reduce<ProgressStatus>((worst, p) => {
            return order.indexOf(p.status) > order.indexOf(worst) ? p.status : worst;
        }, STATUS.SCHEDULED);
    }

    return {
        key: `mod-${m.key}`,
        name: m.name,
        abbrev: m.abbrev,
        color: m.color,
        moduleKey: m.key,
        status,
        actualStart,
        actualEnd,
        projectedStart,
        projectedEnd,
    };
};

interface PhaseLocation {
    moduleKey: string;
    moduleName: string;
    phaseName: string;
}

export interface EventCluster {
    key: string;
    rowKey: string;
    date: Date;
    events: TimelineEvent[];
    fileCount: number;
    commentCount: number;
    noteCount: number;
}

export const clusterEventsByDay = (
    events: TimelineEvent[],
    rowKeyFor: (e: TimelineEvent) => string | null,
): EventCluster[] => {
    const map = new Map<string, EventCluster>();
    events.forEach(e => {
        const rowKey = rowKeyFor(e);
        if (!rowKey) return;
        const d = e.date;
        const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        const k = `${rowKey}|${dayKey}`;
        let c = map.get(k);
        if (!c) {
            const midday = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0);
            c = { key: k, rowKey, date: midday, events: [], fileCount: 0, commentCount: 0, noteCount: 0 };
            map.set(k, c);
        }
        c.events.push(e);
        if (e.type === 'File') c.fileCount++;
        else if (e.type === 'Note') c.noteCount++;
        else if (e.type === 'Comment') c.commentCount++;
    });
    return Array.from(map.values()).sort((a, b) => a.date.getTime() - b.date.getTime());
};

const buildEvents = (
    activity: GrantActivityItemDto[],
    phases: ProgressPhase[],
    steps: ProgressStep[],
    unscheduledPhases: UnscheduledPhase[],
): TimelineEvent[] => {
    const stepById = new Map(steps.map(s => [s.stepId, s]));
    const phaseLocation = new Map<string, PhaseLocation>();
    phases.forEach(p => phaseLocation.set(p.phaseId, { moduleKey: p.moduleKey, moduleName: p.moduleName, phaseName: p.name }));
    unscheduledPhases.forEach(p => phaseLocation.set(p.phaseId, { moduleKey: p.moduleKey, moduleName: p.moduleName, phaseName: p.name }));

    const norm = (s: string | undefined | null) => (s ?? '').toLowerCase().trim();

    const out: TimelineEvent[] = [];
    activity.forEach(a => {
        if (!a.date) return;
        const date = new Date(a.date);
        // Backend uses DateTime.MinValue ("0001-01-01") when DateCreated is null; treat those as invalid.
        if (isNaN(date.getTime()) || date.getFullYear() < 1900) return;

        // Grant-level notes have no phase/step to anchor to a timeline row; they live only in the Notes tab.
        if (!a.phaseId && !a.stepId) return;

        const step = a.stepId ? stepById.get(a.stepId) : undefined;

        // Try the tracked phase map first; if missing, fall back to the activity DTO's own module name
        // so the event still surfaces (orphan row) instead of being silently dropped.
        let loc = a.phaseId ? phaseLocation.get(a.phaseId) : undefined;
        if (!loc) {
            const aModule = norm(a.module);
            const matched = MODULE_CATALOG.find(m => norm(m.name) === aModule || norm(m.abbrev) === aModule);
            loc = {
                moduleKey: matched?.key ?? 'unknown',
                moduleName: matched?.name ?? (a.module || 'Unknown'),
                phaseName: a.phase || '',
            };
        }

        // Deterministic keys mirror the scheduled-row key scheme (`module-phase`, `module-phase-step`)
        // so events land on scheduled rows AND on the unscheduled rows the controller builds.
        const phaseKey = `${loc.moduleKey}-${a.phaseId}`;
        const stepKey = a.stepId ? `${loc.moduleKey}-${a.phaseId}-${a.stepId}` : null;

        // Keep the full comment text: the tooltip truncates it with a CSS ellipsis and shows the
        // whole thing as alt text, so no length cap here.
        const label = a.fileName ?? (a.commentText
            ? a.commentText.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
            : a.type);
        out.push({
            id: a.id,
            moduleKey: loc.moduleKey,
            moduleName: loc.moduleName,
            phaseKey,
            phaseName: loc.phaseName,
            stepKey,
            stepName: step?.name ?? (a.step ?? ''),
            date,
            label,
            userName: a.author ?? '',
            type: a.type,
            commentType: a.commentType,
            fileId: a.fileId,
            fileName: a.fileName,
            resubmission: a.resubmission ?? null,
            grantId: a.grantId,
            moduleTypeId: a.moduleTypeId,
            phaseId: a.phaseId,
            stepId: a.stepId,
        });
    });
    return out;
};

// Switch between two ways of showing an absorbed phase. False: it keeps its own row, with no
// timeline bar, and its events stay on that row. True: its events move to the row of the phase that absorbed it,
// beside that phase's own events, and the collapsed phase and its subtasks leave the chart.
export const MOVE_COLLAPSED_PHASE_EVENTS = true;

// The phase each collapsed document phase was absorbed into: the next phase in the same cycle that
// kept its width. A collapsed phase with none after it is left out, so its events stay where they are.
export const absorbingPhaseKeys = (phases: ProgressPhase[], mergedSpanByPhaseId: Map<string, MergedSpan>): Map<string, string> => {
    const absorbers = new Map<string, string>();
    const cycle = phases.filter(p => mergedSpanByPhaseId.has(p.phaseId));
    cycle.forEach((p, i) => {
        if (!p.collapsed) return;
        const absorber = cycle.slice(i + 1).find(next => next.moduleKey === p.moduleKey && !next.collapsed);
        if (absorber) absorbers.set(p.key, absorber.key);
    });
    return absorbers;
};

// Puts each event of a collapsed phase on its absorbing phase's row. The event keeps its own phase
// name, so its tooltip still says where it came from.
export const moveEventsToAbsorbingPhase = (events: TimelineEvent[], absorbers: Map<string, string>): TimelineEvent[] =>
    events.map(e => {
        const absorber = absorbers.get(e.phaseKey);
        return absorber ? { ...e, phaseKey: absorber } : e;
    });

// Takes each collapsed phase that has an absorbing phase out of the chart: its events go to the
// absorbing phase's row, and the phase and its subtasks are dropped so no empty row is left behind.
export const absorbCollapsedPhases = (
    parts: Pick<ProgressData, 'phases' | 'steps' | 'unscheduledSteps' | 'events'>,
    absorbers: Map<string, string>,
): Pick<ProgressData, 'phases' | 'steps' | 'unscheduledSteps' | 'events' | 'absorbedPhases'> => {
    const absorbed = parts.phases.filter(p => absorbers.has(p.key));
    const absorbedIds = new Set(absorbed.map(p => p.phaseId));
    const nameOf = new Map(parts.phases.map(p => [p.key, p.name]));
    return {
        phases: parts.phases.filter(p => !absorbers.has(p.key)),
        steps: parts.steps.filter(s => !absorbers.has(s.phaseKey)),
        unscheduledSteps: parts.unscheduledSteps.filter(s => !absorbedIds.has(s.phaseId)),
        events: moveEventsToAbsorbingPhase(parts.events, absorbers),
        absorbedPhases: absorbed.map(p => ({
            name: p.name,
            absorbedInto: nameOf.get(absorbers.get(p.key)!) ?? '',
            moduleKey: p.moduleKey,
        })),
    };
};

export const buildProgressData = (raw: RawProgressData): ProgressData | null => {
    if (!raw.modules.length) return null;

    const phases: ProgressPhase[] = [];
    const unscheduledPhases: UnscheduledPhase[] = [];
    // Context for every phase — dated or not — so its subtasks get built below. An undated task
    // still owns subtasks that carry their own dates, so subtask building can't skip it.
    const phaseContexts: { context: StepPhaseContext; dto: PhaseDto }[] = [];
    raw.modules.forEach(m => {
        const dtos = raw.phasesByModule.get(m.key) ?? [];
        dtos.forEach(dto => {
            const p = buildPhase(dto, m, raw.mergedSpanByPhaseId.get(dto.id));
            if (p) {
                phases.push(p);
            } else {
                unscheduledPhases.push({
                    phaseId: dto.id,
                    name: dto.defaultPhase?.name ?? dto.name,
                    moduleKey: m.key,
                    moduleAbbrev: m.abbrev,
                    moduleName: m.name,
                });
            }
            phaseContexts.push({
                context: {
                    key: `${m.key}-${dto.id}`,
                    phaseId: dto.id,
                    name: dto.defaultPhase?.name ?? dto.name,
                    moduleKey: m.key,
                    moduleName: m.name,
                    moduleAbbrev: m.abbrev,
                },
                dto,
            });
        });
    });
    if (!phases.length && !unscheduledPhases.length) return null;

    const steps: ProgressStep[] = [];
    const unscheduledSteps: UnscheduledStep[] = [];
    phaseContexts.forEach(({ context, dto }) => {
        const stepDtos = raw.stepsByPhaseId.get(dto.id) ?? [];
        stepDtos.forEach(sd => {
            const s = buildStep(sd, context);
            if (s) {
                steps.push(s);
            } else {
                unscheduledSteps.push({
                    stepId: sd.id,
                    phaseId: context.phaseId,
                    name: sd.defaultStep?.name ?? sd.name,
                    phaseName: context.name,
                    moduleKey: context.moduleKey,
                    moduleAbbrev: context.moduleAbbrev,
                    moduleName: context.moduleName,
                });
            }
        });
    });

    const moduleAggregates: ProgressModuleAggregate[] = [];
    raw.modules.forEach(m => {
        const agg = aggregateModule(m, phases);
        if (agg) moduleAggregates.push(agg);
    });

    const events = buildEvents(raw.activity, phases, steps, unscheduledPhases);

    const allDates: Date[] = [];
    phases.forEach(p => allDates.push(p.projectedStart, p.projectedEnd, p.actualStart, p.actualEnd));
    steps.forEach(s => allDates.push(s.projectedStart, s.projectedEnd, s.actualStart, s.actualEnd));
    events.forEach(e => allDates.push(e.date));
    // Fallback range if only unscheduled items exist: today ± 3 months.
    const DAY_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const fallbackMin = now - 90 * DAY_MS;
    const fallbackMax = now + 90 * DAY_MS;
    const rawMin = allDates.length ? Math.min(...allDates.map(d => d.getTime())) : fallbackMin;
    const rawMax = allDates.length ? Math.max(...allDates.map(d => d.getTime())) : fallbackMax;
    // Pad the range so an event sitting on the first/last date isn't clipped by the panel edge at
    // full zoom-out — the triangle is centered on its date, so without padding half of it falls
    // outside the viewport. Scale the pad to the span (min one day for very short ranges).
    const pad = Math.max(DAY_MS, (rawMax - rawMin) * 0.03);
    const rangeMin = new Date(rawMin - pad);
    const rangeMax = new Date(rawMax + pad);

    // The date range and module summary above are worked out first, so hiding an absorbed phase
    // does not shift the chart.
    const shown = MOVE_COLLAPSED_PHASE_EVENTS
        ? absorbCollapsedPhases({ phases, steps, unscheduledSteps, events }, absorbingPhaseKeys(phases, raw.mergedSpanByPhaseId))
        : { phases, steps, unscheduledSteps, events, absorbedPhases: [] };

    return { ...shown, rangeMin, rangeMax, modules: raw.modules, moduleAggregates, unscheduledPhases };
};
